import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import type { Role } from "../api/types";

interface ProtectedRouteProps {
  children: ReactNode;
  requireRole?: Role[];
  /** Most pages operate on tenant data; a PLATFORM_SUPERADMIN with no tenant (tenantId: null)
   *  hitting one gets a raw "No tenant context for this request" API error instead of a page.
   *  Defaults true - opt out (e.g. /admin/tenants) with requireTenant={false}. */
  requireTenant?: boolean;
}

export function ProtectedRoute({ children, requireRole, requireTenant = true }: ProtectedRouteProps) {
  const { isAuthenticated, hasRole, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireTenant && !user?.tenantId) {
    return <Navigate to="/admin/overview" replace />;
  }

  if (requireRole && requireRole.length > 0 && !hasRole(...requireRole)) {
    return (
      <div className="page-panel">
        <h2>Not authorized</h2>
        <p>You don't have permission to view this page.</p>
      </div>
    );
  }

  return <>{children}</>;
}
